import React from "react";
import {
  Box,
  TextField,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { ConversationAnalysisStyles } from "./ConversationAnalysisStyles";
import { IConversationFilters } from "../../models/admin/conversationAnalysis/conversationAnalysis";

interface IConversationFiltersProps {
  filters: IConversationFilters;
  handleDateChange: (type: "from" | "to", value: string) => void;
}

const ConversationFilters: React.FC<IConversationFiltersProps> = ({
  filters,
  handleDateChange,
}) => {
  const isMobile = useMediaQuery("(max-width:767px)");

  const todayDate = new Date().toISOString().split("T")[0];

  const formatDate = (date: string | Date | undefined) => {
    if (!date) return "";
    if (typeof date === "string") {
      return date.split("T")[0];
    }
    if (date instanceof Date) {
      return date.toISOString().split("T")[0];
    }
    return "";
  };

  const handleToDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedDate = e.target.value;

    if (!selectedDate) {
      handleDateChange("to", "");
      return;
    }

    const date = new Date(selectedDate);
    date.setHours(23, 59, 59, 999);

    const adjustedDate = new Date(
      date.getTime() - date.getTimezoneOffset() * 60000
    );
    const formattedDateTime = adjustedDate.toISOString();
    handleDateChange("to", formattedDateTime);
  };

  return (
    <Box
      sx={{
        ...ConversationAnalysisStyles.filterContainer,
        maxWidth: isMobile ? "calc(100vw - 40px)" : "none",
      }}
      id="conversation_filters_container"
      data-test-id="conversation_filters_container"
      aria-label="Conversation Filters"
    >
      <Typography
        variant="h6"
        sx={{
          background: (theme: Theme) =>
            `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          fontWeight: "bold",
        }}
        id="conversation_filters_title"
        data-test-id="conversation_filters_title"
        aria-label="Conversation Analysis"
      >
        Conversation Analysis
      </Typography>
      <Box 
        sx={ConversationAnalysisStyles.dateFieldsContianer}
        id="conversation_date_fields"
        data-test-id="conversation_date_fields"
        aria-label="Date Range Filters"
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
          }}
          id="conversation_from_date_container"
          data-test-id="conversation_from_date_container"
          aria-label="From Date Filter"
        >
          <TextField
            size="small"
            fullWidth
            type="date"
            label="From"
            value={formatDate(filters.fromDate)}
            onChange={(e) => {
              const selectedDate = e.target.value;
              handleDateChange("from", selectedDate);
            }}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              max: todayDate,
            }}
            id="conversation_from_date"
            data-test-id="conversation_from_date"
            aria-label="From Date"
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
          }}
          id="conversation_to_date_container"
          data-test-id="conversation_to_date_container"
          aria-label="To Date Filter"
        >
          <TextField
            size="small"
            fullWidth
            type="date"
            label="To"
            value={formatDate(filters.toDate)}
            onChange={handleToDateChange}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              max: todayDate,
            }}
            id="conversation_to_date"
            data-test-id="conversation_to_date"
            aria-label="To Date"
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ConversationFilters;
